<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';

import RenderCanvas from '@/components/renderCanvases/RenderCanvas.vue';
import CircleDotSolid from '@/components/svg/CircleDotSolid.vue';
import OnboardingTooltip from '@/components/utilities/OnboardingTooltip.vue';
import type { Building } from '@/typings/buildings/Building';
import type { FacadeRenderLayer } from '@/typings/drawing/FacadeRenderLayer';
import type { Unit } from '@/typings/units/Unit';
import { getBackgroundImageUrl } from '@/utils/canvas';
import { getUnitStatusString } from '@/enums/UnitStatus';

// Generic layer type that can be facade render layer
type RenderLayer = FacadeRenderLayer;

// Models pour la sélection (peuvent être Unit selon le mode)
const selectedItem = defineModel<Unit | undefined>('selectedItem');
const hoveredItem = defineModel<Unit | undefined>('hoveredItem');

const canvasElement = useTemplateRef<HTMLDivElement>('canvasElement');

defineExpose({
  canvasElement
});

const props = defineProps<{
  layers: RenderLayer[];
  items?: Unit[]; // Optional, pour la compatibilité avec l'ancien système
  building?: Building;
  imageUrl?: string; // URL d'image personnalisée
  hideOnboardingTooltip?: boolean;
}>();

const emit = defineEmits<{
  'canvas-loaded': [];
  'item-click': [item: Unit];
  'layer-click': [layer: RenderLayer]; // Événement pour les clics directs sur les couches
}>();

const canvasLoaded = ref(false);
const showOnboarding = ref(false);

const backgroundImageUrl = computed(() => {
  if (props.imageUrl) {
    return getBackgroundImageUrl(props.imageUrl);
  }
  if (props.building?.buildingImageUrl) {
    return getBackgroundImageUrl(props.building.buildingImageUrl);
  }
  return props.layers[0]?.imageUrl || '';
});

const isLayerSelected = (layer: RenderLayer) => {
  return selectedItem.value?.id === (layer as FacadeRenderLayer).unitId;
};

const isLayerHovered = (layer: RenderLayer) => {
  return hoveredItem.value?.id === (layer as FacadeRenderLayer).unitId;
};

function handleLayerClick(layer: RenderLayer) {
  emit('layer-click', layer);
}

function handleLayerHover(layer: RenderLayer | undefined) {
  if (!layer) {
    hoveredItem.value = undefined;
    return;
  }
}

function onCanvasLoaded() {
  canvasLoaded.value = true;
  emit('canvas-loaded');
  
  setTimeout(() => {
    showOnboarding.value = props.hideOnboardingTooltip === true ? false : true;
  }, 500);
}

function getLayerAriaLabel(layer: RenderLayer) {
  const facadeLayer = layer as FacadeRenderLayer;
  return `Unité ${facadeLayer.unitNumber}, statut: ${facadeLayer.status}`;
}

function getLayerDataStatus(layer: RenderLayer): string {
  return getUnitStatusString((layer as FacadeRenderLayer).status);
}
</script>

<template>
  <div ref="canvasElement"
       class="renderCanvas-container">
    <RenderCanvas class="renderCanvas_facade"
                  :layers="layers"
                  :image-url="backgroundImageUrl"
                  @canvas-loaded="onCanvasLoaded">
      <template #point="{ layer, point, handleClick, onMouseenter, onMouseleave }">
        <g class="renderCanvas-pointItem"
           :data-item-status="getLayerDataStatus(layer)"
           :data-selected="isLayerSelected(layer)"
           :data-hovered="isLayerHovered(layer)">
          <foreignObject
            :x="point.points[0].x - 16"
            :y="point.points[0].y - 16"
            width="32"
            height="32"
            class="renderCanvas-polygonShape"
            role="button"
            :tabindex="0"
            :aria-label="getLayerAriaLabel(layer)"
            :aria-pressed="isLayerSelected(layer)"
            @click="(event) => { handleLayerClick(layer); handleClick(); }"
            @mouseenter="(event) => { handleLayerHover(layer); onMouseenter(event); }"
            @mouseleave="(event) => { handleLayerHover(undefined); onMouseleave(); }"
            @keydown.enter="handleLayerClick(layer)"
            @keydown.space="handleLayerClick(layer)">
            <div class="renderCanvas-pointIcon">
              <CircleDotSolid />
            </div>
          </foreignObject>
        </g>
      </template>
      <template #tooltip="{ layer }">
        <slot name="tooltip" :layer="layer"></slot>
      </template>
    </RenderCanvas>
    <OnboardingTooltip :visible="showOnboarding"
                       :to-click-element="canvasElement ?? undefined"
                       message-key="onboarding.canvas.unitSelection"
                       storage-key="gesteco-onboarding-seen-building" />
  </div>
</template>

<style lang="scss">
.renderCanvas-container {
  position: relative;
}

.renderCanvas_floor {
  --baseColor: var(--gray-100);
}

.renderCanvas_floor [data-item-status="Available"] {
  --baseColor: var(--clrGreenStatus);
}

.renderCanvas_floor [data-item-status="Reserved"] {
  --baseColor: var(--clrOrangeStatus);
}

.renderCanvas_floor [data-item-status="Inactive"] {
  --baseColor: var(--clrRedStatus);
}

.renderCanvas_facade {
  --baseColor: var(--gray-100);
}

.renderCanvas_facade [data-item-status="Available"] {
  --baseColor: var(--clrGreenStatus);
}

.renderCanvas_facade [data-item-status="Reserved"] {
  --baseColor: var(--clrOrangeStatus);
}

.renderCanvas_facade [data-item-status="Sold"] {
  --baseColor: var(--clrRedStatus);
}

.renderCanvas_facade [data-item-status="Rented"] {
  --baseColor: var(--clrRedStatus);
}

.renderCanvas_facade [data-item-status="Upcoming"] {
  --baseColor: var(--clrYellowStatus);
}

.unitSummary-planFrame{
  .renderCanvas_facade{
    border:0;
  }
}

.unitSummary-planDisplay{
  .renderCanvas_facade{
    border:0;
  }
}
</style>
